import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDetailBack } from '../../../hooks/useDetailBack';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../Common/LoadingSpinner';
import ConfirmationModal from '../../Common/ConfirmationModal';
import inventoryApi from '../../../services/inventoryApi';
import adminAPI from '../../../services/adminApi';
import { openSupplierPaymentReport } from '../../../utils/openInventoryReport';
import { canPrintSupplierPayment } from '../../../utils/documentPrintGate';

const SupplierPaymentView = ({ paymentId: propPaymentId, onClose }) => {
  const { id: paramId } = useParams();
  const handleBack = useDetailBack('/inventory/supplier-payments');
  const isModal = Boolean(propPaymentId);
  const id = propPaymentId || paramId;

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [actionComment, setActionComment] = useState('');
  const [companyInfo, setCompanyInfo] = useState({});

  useEffect(() => {
    if (id) fetchPayment();
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminAPI.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setCompanyInfo({
          name: loc.name || loc.locationName || '',
          street: addr.street || addr.addressLine1 || '',
          city: addr.city || '',
          zipCode: addr.zipCode || addr.postalCode || '',
        });
      } catch (e) {
        console.warn('[SupplierPaymentView] could not load location info:', e.message);
      }
    })();
  }, []);

  const handlePrint = async () => {
    await openSupplierPaymentReport(payment, companyInfo);
  };

  const fetchPayment = async () => {
    try {
      setLoading(true);
      const response = await inventoryApi.getSupplierPayment(id);
      setPayment(response.data);
    } catch (err) {
      console.error('Error fetching payment:', err);
      toast.error('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (isModal && onClose) onClose();
    else handleBack();
  };

  const handleApprove = () => {
    setActionComment('');
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    try {
      setApproving(true);
      await inventoryApi.approveSupplierPayment(id, { comments: actionComment || 'Approved' });
      toast.success('Payment approved');
      setShowApproveModal(false);
      fetchPayment();
    } catch (err) {
      toast.error('Failed to approve payment');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = () => {
    setActionComment('');
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    try {
      setApproving(true);
      await inventoryApi.rejectSupplierPayment(id, { comments: actionComment || 'Rejected' });
      toast.success('Payment rejected');
      setShowRejectModal(false);
      fetchPayment();
    } catch (err) {
      toast.error('Failed to reject payment');
    } finally {
      setApproving(false);
    }
  };

  const handleProcess = () => {
    setActionComment('');
    setShowProcessModal(true);
  };

  const confirmProcess = async () => {
    try {
      setProcessing(true);
      await inventoryApi.processSupplierPayment(id, {});
      toast.success('Payment processed');
      setShowProcessModal(false);
      fetchPayment();
    } catch (err) {
      toast.error('Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    const map = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      paid: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const sectionHeader = (title, extra) => (
    <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5">
      <span className="text-xs font-semibold text-white">{title}</span>
      {extra}
    </div>
  );

  const fieldLabel = 'block text-xs font-medium text-gray-500 mb-0.5';
  const fieldValue = 'text-sm font-medium text-gray-900';

  // ── Loading / Error states ─────────────────────────────────────────────────
  if (loading) {
    if (isModal) {
      return (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl max-h-[90vh] bg-white shadow-2xl rounded-2xl overflow-hidden flex flex-col">
            <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 rounded-t-2xl">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">Payment Details</h3>
              </div>
            </div>
            <LoadingSpinner />
          </div>
        </div>
      );
    }
    return <LoadingSpinner />;
  }

  if (!payment) {
    return isModal ? null : (
      <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-500">Payment not found.</div>
    );
  }

  // ── Content sections ───────────────────────────────────────────────────────
  const paymentInfoSection = (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
      {sectionHeader('Payment Information')}
      <div className="px-3 py-2">
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className={fieldLabel}>Voucher No.</label>
            <p className={fieldValue}>{payment.paymentVoucherNo || '—'}</p>
          </div>
          <div>
            <label className={fieldLabel}>Payment Date</label>
            <p className={fieldValue}>{formatDate(payment.paymentDate)}</p>
          </div>
          <div>
            <label className={fieldLabel}>Supplier</label>
            <p className={fieldValue}>{payment.supplier?.name || '—'}</p>
            {payment.supplier?.code && <p className="text-xs text-gray-400">{payment.supplier.code}</p>}
          </div>
          <div>
            <label className={fieldLabel}>Payment Type</label>
            <p className={fieldValue}>{payment.paymentType?.replace(/_/g, ' ') || '—'}</p>
          </div>
        </div>
        {payment.notes && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <label className={fieldLabel}>Notes</label>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{payment.notes}</p>
          </div>
        )}
      </div>
    </div>
  );

  const grnSection = payment.grnAllocations?.length > 0 && (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
      {sectionHeader(`GRN Allocations (${payment.grnAllocations.length})`)}
      <div className="overflow-x-auto">
        <table className="min-w-max w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['GRN Number', 'Date', 'GRN Amount', 'Outstanding', 'Paying'].map(h => (
                <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payment.grnAllocations.map((a, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium text-gray-900">{a.grnNo || '—'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{formatDate(a.grnDate)}</td>
                <td className="px-4 py-2 text-sm text-gray-700 text-right">{parseFloat(a.grnAmount || 0).toFixed(2)}</td>
                <td className="px-4 py-2 text-sm text-gray-700 text-right">{parseFloat(a.outstandingBalance || 0).toFixed(2)}</td>
                <td className="px-4 py-2 text-sm font-semibold text-indigo-700 text-right">{parseFloat(a.amountPaid || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const srnSection = payment.srnAllocations?.length > 0 && (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
      {sectionHeader(`SRN Setoffs (${payment.srnAllocations.length})`)}
      <div className="overflow-x-auto">
        <table className="min-w-max w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['SRN Number', 'Date', 'Return Value', 'Setoff'].map(h => (
                <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payment.srnAllocations.map((a, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium text-gray-900">{a.srnNo || '—'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{formatDate(a.srnDate)}</td>
                <td className="px-4 py-2 text-sm text-gray-700 text-right">{parseFloat(a.returnValue || 0).toFixed(2)}</td>
                <td className="px-4 py-2 text-sm font-semibold text-orange-600 text-right">-{parseFloat(a.amountSetoff || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const debitNoteSection = payment.debitNoteAllocations?.length > 0 && (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
      {sectionHeader(`Debit Note Setoffs (${payment.debitNoteAllocations.length})`)}
      <div className="overflow-x-auto">
        <table className="min-w-max w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Debit Note No.', 'Date', 'Note Value', 'Setoff'].map(h => (
                <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payment.debitNoteAllocations.map((a, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium text-gray-900">{a.debitNoteNo || '—'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{formatDate(a.debitDate)}</td>
                <td className="px-4 py-2 text-sm text-gray-700 text-right">{parseFloat(a.noteValue || 0).toFixed(2)}</td>
                <td className="px-4 py-2 text-sm font-semibold text-amber-600 text-right">-{parseFloat(a.amountSetoff || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const paymentModesSection = payment.paymentModes?.length > 0 && (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
      {sectionHeader('Payment Modes')}
      <div className="overflow-x-auto">
        <table className="min-w-max w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Method', 'Ledger Account', 'Amount', 'Details', 'Notes'].map(h => (
                <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payment.paymentModes.map((m, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium text-gray-900 capitalize">{m.paymentMethod?.replace(/_/g, ' ') || '—'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{m.ledgerAccountLabel || '—'}</td>
                <td className="px-4 py-2 text-sm font-semibold text-green-700 text-right">{parseFloat(m.amount || 0).toFixed(2)}</td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  {m.paymentMethod === 'cheque' && m.chequeDetails?.chequeNo && (
                    <span>Chq: {m.chequeDetails.chequeNo}{m.chequeDetails.chequeDate ? ` (${formatDate(m.chequeDetails.chequeDate)})` : ''}</span>
                  )}
                  {m.paymentMethod === 'bank_transfer' && m.transferDetails?.referenceNo && (
                    <span>Ref: {m.transferDetails.referenceNo}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-gray-500">{m.notes || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const summarySection = (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
      {sectionHeader('Payment Summary')}
      <div className="px-3 py-2">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className={fieldLabel}>GRN Total</label>
            <p className={fieldValue}>{parseFloat(payment.summary?.totalPaymentAmount || 0).toFixed(2)}</p>
          </div>
          {(payment.summary?.adjustments !== 0) && (
            <div>
              <label className={fieldLabel}>Adjustments</label>
              <p className={`${fieldValue} ${payment.summary?.adjustments > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {payment.summary?.adjustments > 0 ? '+' : ''}{parseFloat(payment.summary?.adjustments || 0).toFixed(2)}
              </p>
            </div>
          )}
          {payment.summary?.creditNotesApplied !== 0 && (
            <div>
              <label className={fieldLabel}>Credit Notes</label>
              <p className={`${fieldValue} text-purple-600`}>-{parseFloat(payment.summary?.creditNotesApplied || 0).toFixed(2)}</p>
            </div>
          )}
          {payment.summary?.totalSRNSetoffs !== 0 && (
            <div>
              <label className={fieldLabel}>SRN Setoffs</label>
              <p className={`${fieldValue} text-orange-600`}>-{parseFloat(payment.summary?.totalSRNSetoffs || 0).toFixed(2)}</p>
            </div>
          )}
          {payment.summary?.totalDebitNoteSetoffs !== 0 && (
            <div>
              <label className={fieldLabel}>Debit Note Setoffs</label>
              <p className={`${fieldValue} text-amber-600`}>-{parseFloat(payment.summary?.totalDebitNoteSetoffs || 0).toFixed(2)}</p>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600">
        <span className="text-xs font-bold text-indigo-100 uppercase tracking-wider">Net Payment Amount</span>
        <span className="text-lg font-black text-white">{parseFloat(payment.summary?.netPaymentAmount || 0).toFixed(2)}</span>
      </div>
    </div>
  );

  const peopleSection = (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
      {sectionHeader('Activity')}
      <div className="px-3 py-2 space-y-2">
        {[
          { label: 'Created', by: payment.createdByUserName, at: payment.createdAt, color: 'bg-blue-100 text-blue-600' },
          payment.updatedByUserName && { label: 'Updated', by: payment.updatedByUserName, at: payment.updatedAt, color: 'bg-yellow-100 text-yellow-600' },
          (payment.approvedByUserName || payment.approvedBy) && { label: 'Approved', by: payment.approvedByUserName || payment.approvedBy?.name, at: payment.approvedAt, color: 'bg-green-100 text-green-600' },
          payment.processedBy && { label: 'Processed', by: payment.processedBy?.name, at: payment.processedAt, color: 'bg-purple-100 text-purple-600' }
        ].filter(Boolean).map((item, i) => (
          <div key={i} className="flex items-center space-x-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${item.color}`}>
              <span className="text-xs font-semibold">{item.by?.charAt(0) || '?'}</span>
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-medium text-gray-700">{item.label}: </span>
              <span className="text-xs text-gray-600">{item.by || '—'}</span>
              <span className="text-xs text-gray-400 ml-1">· {formatDateTime(item.at)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ── Action buttons ─────────────────────────────────────────────────────────
  const actionButtons = (
    <div className="flex items-center gap-2">
      {(payment.status === 'pending' || payment.status === 'draft') && (
        <>
          <button
            onClick={handleReject}
            disabled={approving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-300 text-red-700 bg-white hover:bg-red-50 rounded transition-colors disabled:opacity-50"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Reject
          </button>
          <button
            onClick={handleApprove}
            disabled={approving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-green-300 text-green-700 bg-white hover:bg-green-50 rounded transition-colors disabled:opacity-50"
          >
            {approving ? (
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : (
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {approving ? 'Approving…' : 'Approve'}
          </button>
        </>
      )}
      {payment.status === 'approved' && (
        <button
          onClick={handleProcess}
          disabled={processing}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50"
        >
          {processing ? (
            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          )}
          {processing ? 'Processing…' : 'Process Payment'}
        </button>
      )}
      {canPrintSupplierPayment(payment.status) && (
        <button
          onClick={handlePrint}
          title="Print PDF"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded transition-colors"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print PDF
        </button>
      )}
      <button
        onClick={handleClose}
        className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
      >
        Close
      </button>
    </div>
  );

  // ── Shared confirmation modals (used in both modal and page mode) ──────────
  const confirmationModals = (
    <>
      <ConfirmationModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={confirmApprove}
        title="Approve Supplier Payment"
        message={
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Are you sure you want to approve payment{' '}
              <span className="font-semibold text-gray-900">{payment?.paymentVoucherNo}</span>?
            </p>
            <p className="text-xs text-gray-500">
              Net Amount:{' '}
              <span className="font-semibold text-gray-800">
                {parseFloat(payment?.summary?.netPaymentAmount || 0).toFixed(2)}
              </span>
            </p>
          </div>
        }
        confirmText="Approve"
        cancelText="Cancel"
        type="success"
        loading={approving}
        showCommentBox={true}
        comment={actionComment}
        onCommentChange={(e) => setActionComment(e.target.value)}
      />

      <ConfirmationModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={confirmReject}
        title="Reject Supplier Payment"
        message={
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Are you sure you want to reject payment{' '}
              <span className="font-semibold text-gray-900">{payment?.paymentVoucherNo}</span>?
            </p>
            <p className="text-xs text-orange-600">The payment will be returned to draft status.</p>
          </div>
        }
        confirmText="Reject"
        cancelText="Cancel"
        type="danger"
        loading={approving}
        showCommentBox={true}
        comment={actionComment}
        onCommentChange={(e) => setActionComment(e.target.value)}
        disableConfirm={!actionComment.trim()}
      />

      <ConfirmationModal
        isOpen={showProcessModal}
        onClose={() => setShowProcessModal(false)}
        onConfirm={confirmProcess}
        title="Process Payment"
        message={
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Are you sure you want to process payment{' '}
              <span className="font-semibold text-gray-900">{payment?.paymentVoucherNo}</span>?
            </p>
            <p className="text-xs text-red-600 font-medium">This action cannot be undone.</p>
          </div>
        }
        confirmText="Process Payment"
        cancelText="Cancel"
        type="warning"
        loading={processing}
      />
    </>
  );

  // ── Modal mode ─────────────────────────────────────────────────────────────
  if (isModal) {
    return (
      <>
      <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-5xl max-h-[90vh] bg-white shadow-2xl border-0 flex flex-col rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 border-b border-gray-200 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Payment Details</h3>
                  <p className="text-indigo-100 text-sm mt-1">Voucher #{payment.paymentVoucherNo}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                  {payment.status?.toUpperCase()}
                </span>
                <button
                  onClick={handleClose}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 pt-3 pb-2">
            {paymentInfoSection}
            {grnSection}
            {srnSection}
            {debitNoteSection}
            {paymentModesSection}
            {summarySection}
            {peopleSection}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50/60">
            {actionButtons}
          </div>
        </div>
      </div>
      {confirmationModals}
      </>
    );
  }

  // ── Page mode (standalone route) ───────────────────────────────────────────
  return (
    <>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{payment.paymentVoucherNo}</h1>
              <p className="text-sm text-gray-500">Created on {formatDateTime(payment.createdAt)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
              {payment.status?.toUpperCase()}
            </span>
            {actionButtons}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main — 2 cols */}
        <div className="lg:col-span-2 space-y-6">
          {/* Payment Information */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Payment Information</h3>
            </div>
            <div className="px-6 py-4">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Voucher Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{payment.paymentVoucherNo}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Payment Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(payment.paymentDate)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Supplier</dt>
                  <dd className="mt-1 text-sm font-medium text-gray-900">{payment.supplier?.name || '—'}</dd>
                  {payment.supplier?.code && <dd className="text-xs text-gray-500">{payment.supplier.code}</dd>}
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Payment Type</dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">{payment.paymentType?.replace(/_/g, ' ') || '—'}</dd>
                </div>
              </dl>
              {payment.notes && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <dt className="text-sm font-medium text-gray-500 mb-1">Notes</dt>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{payment.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* GRN Allocations */}
          {payment.grnAllocations?.length > 0 && (
            <div className="bg-white shadow-sm rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">GRN Allocations</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-max w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['GRN Number', 'Date', 'GRN Amount', 'Outstanding', 'Paying'].map(h => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payment.grnAllocations.map((a, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{a.grnNo || '—'}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{formatDate(a.grnDate)}</td>
                        <td className="px-6 py-4 text-sm text-gray-700 text-right">{parseFloat(a.grnAmount || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm text-gray-700 text-right">{parseFloat(a.outstandingBalance || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-indigo-700 text-right">{parseFloat(a.amountPaid || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SRN Setoffs */}
          {payment.srnAllocations?.length > 0 && (
            <div className="bg-white shadow-sm rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Supplier Return Note Setoffs</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-max w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['SRN Number', 'Date', 'Return Value', 'Setoff'].map(h => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payment.srnAllocations.map((a, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{a.srnNo || '—'}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{formatDate(a.srnDate)}</td>
                        <td className="px-6 py-4 text-sm text-gray-700 text-right">{parseFloat(a.returnValue || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-orange-600 text-right">-{parseFloat(a.amountSetoff || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Debit Note Setoffs */}
          {payment.debitNoteAllocations?.length > 0 && (
            <div className="bg-white shadow-sm rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Debit Note Setoffs</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-max w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Debit Note No.', 'Date', 'Note Value', 'Setoff'].map(h => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payment.debitNoteAllocations.map((a, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{a.debitNoteNo || '—'}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{formatDate(a.debitDate)}</td>
                        <td className="px-6 py-4 text-sm text-gray-700 text-right">{parseFloat(a.noteValue || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-amber-600 text-right">-{parseFloat(a.amountSetoff || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payment Modes */}
          {payment.paymentModes?.length > 0 && (
            <div className="bg-white shadow-sm rounded-lg border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Payment Modes</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-max w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Method', 'Ledger Account', 'Amount', 'Details', 'Notes'].map(h => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payment.paymentModes.map((m, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 capitalize">{m.paymentMethod?.replace(/_/g, ' ') || '—'}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{m.ledgerAccountLabel || '—'}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-green-700 text-right">{parseFloat(m.amount || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {m.paymentMethod === 'cheque' && m.chequeDetails?.chequeNo && (
                            <span>Chq: {m.chequeDetails.chequeNo}{m.chequeDetails.chequeDate ? ` · ${formatDate(m.chequeDetails.chequeDate)}` : ''}</span>
                          )}
                          {m.paymentMethod === 'bank_transfer' && m.transferDetails?.referenceNo && (
                            <span>Ref: {m.transferDetails.referenceNo}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{m.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar — 1 col */}
        <div className="space-y-6">
          {/* Payment Summary */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Payment Summary</h3>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Total Payment</span>
                <span className="text-sm font-medium text-gray-900">{parseFloat(payment.summary?.totalPaymentAmount || 0).toFixed(2)}</span>
              </div>
              {payment.summary?.adjustments !== 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Adjustments</span>
                  <span className={`text-sm font-medium ${payment.summary?.adjustments > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {payment.summary?.adjustments > 0 ? '+' : ''}{parseFloat(payment.summary?.adjustments || 0).toFixed(2)}
                  </span>
                </div>
              )}
              {payment.summary?.creditNotesApplied !== 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Credit Notes</span>
                  <span className="text-sm font-medium text-purple-600">-{parseFloat(payment.summary?.creditNotesApplied || 0).toFixed(2)}</span>
                </div>
              )}
              {payment.summary?.totalSRNSetoffs !== 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">SRN Setoffs</span>
                  <span className="text-sm font-medium text-orange-600">-{parseFloat(payment.summary?.totalSRNSetoffs || 0).toFixed(2)}</span>
                </div>
              )}
              {payment.summary?.totalDebitNoteSetoffs !== 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Debit Note Setoffs</span>
                  <span className="text-sm font-medium text-amber-600">-{parseFloat(payment.summary?.totalDebitNoteSetoffs || 0).toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-900">Net Payment</span>
                  <span className="text-xl font-bold text-green-600">{parseFloat(payment.summary?.netPaymentAmount || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* People Involved */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">People Involved</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              {[
                { label: 'Created By', by: payment.createdByUserName, at: payment.createdAt, color: 'bg-blue-100 text-blue-600' },
                payment.updatedByUserName && { label: 'Last Updated By', by: payment.updatedByUserName, at: payment.updatedAt, color: 'bg-yellow-100 text-yellow-600' },
                (payment.approvedByUserName || payment.approvedBy) && { label: 'Approved By', by: payment.approvedByUserName || payment.approvedBy?.name, at: payment.approvedAt, color: 'bg-green-100 text-green-600' },
                payment.processedBy && { label: 'Processed By', by: payment.processedBy?.name, at: payment.processedAt, color: 'bg-purple-100 text-purple-600' }
              ].filter(Boolean).map((item, i) => (
                <div key={i}>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{item.label}</label>
                  <div className="flex items-center space-x-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${item.color}`}>
                      <span className="text-sm font-medium">{item.by?.charAt(0) || '?'}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-900">{item.by || '—'}</span>
                      <div className="text-xs text-gray-500">{formatDateTime(item.at)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
    {confirmationModals}
    </>
  );
};

export default SupplierPaymentView;
